import { useMutation } from '@apollo/client';

import { useAscContext } from '@neo.tax/asc/context';
import { type AscWage } from '@neo.tax/asc/hooks';
import {
  APPLY_WEIGHTED_CAPITALIZED_PERCENTAGE_TO_CONTRIBUTORS,
  GetAscWagesQueryVariables,
  GET_ASC_WAGES,
  ApplyWeightedCapitalizedPercentageToContributorsMutation,
  ApplyWeightedCapitalizedPercentageToContributorsMutationVariables,
  advancedFilter,
  AscWageFilter,
  keywordFilter,
  AscWageSort,
  Conditions,
  FilterField,
  evictCapitalizedAllocation,
} from '@neo.tax/shared';

interface UseApplyWeightedCapitalizedPercentageToContributorsProps {
  filterConditions: Conditions<FilterField<AscWageFilter>>;
  searchKeyword: string | null;
  sorting: Array<AscWageSort> | AscWageSort;
}

interface UseApplyWeightedCapitalizedPercentageToContributorsReturn {
  isAllocating: boolean;
  applyAllocation: (selectedWages: AscWage[], allocationPercent: number) => Promise<boolean>;
}

export const useApplyWeightedCapitalizedPercentageToContributors = ({
  filterConditions,
  searchKeyword,
  sorting,
}: UseApplyWeightedCapitalizedPercentageToContributorsProps): UseApplyWeightedCapitalizedPercentageToContributorsReturn => {
  const { fiscalYearId, periodName } = useAscContext();
  const [applyWeightedCapitalizedPercentageToContributorsMutation, { loading: isAllocating }] = useMutation<
    ApplyWeightedCapitalizedPercentageToContributorsMutation,
    ApplyWeightedCapitalizedPercentageToContributorsMutationVariables
  >(APPLY_WEIGHTED_CAPITALIZED_PERCENTAGE_TO_CONTRIBUTORS, {
    context: {
      successToast: {
        title: 'Allocation updated successfully',
      },
      errorToast: {
        title: 'Error updating allocation',
        description: 'Something unexpected prevented your changes from saving. Please try again.',
      },
    },
    update: () => {
      evictCapitalizedAllocation();
    },
    refetchQueries: [
      {
        query: GET_ASC_WAGES,
        variables: {
          filter: {
            and: [
              keywordFilter(['name', 'titles', 'departments'], searchKeyword),
              advancedFilter<AscWageFilter>(filterConditions),
            ],
          },
          sorting,
          fiscalYearId,
          periodName,
        } satisfies GetAscWagesQueryVariables,
      },
    ],
    awaitRefetchQueries: true,
  });

  const applyAllocation = async (selectedWages: AscWage[], allocationPercent: number): Promise<boolean> => {
    const contributorIds = selectedWages.map((wage) => wage.contributorId);

    await applyWeightedCapitalizedPercentageToContributorsMutation({
      variables: {
        fiscalYearId,
        periodName,
        input: {
          contributorIds,
          desiredCapitalizedPercentage: allocationPercent,
        },
      },
    });

    return true;
  };

  return {
    isAllocating,
    applyAllocation,
  };
};
